import React, { useState, useEffect } from 'react';
import { FiEdit2, FiSave, FiUpload, FiUser, FiLock, FiShield, FiCheck, FiX, FiAlertCircle } from 'react-icons/fi';
import '../../styles/Admin/Profile.css';
import { 
  getCurrentUser, 
  getUserKYCDocuments,
  uploadKYCDocument
} from '../../lib/supabase/helpers';
import { supabase } from '../../lib/supabase/client';

// Inline toast notification component
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${type}`}>
      <span className="toast-icon">
        {type === 'success' ? <FiCheck /> : type === 'error' ? <FiX /> : <FiAlertCircle />}
      </span>
      <span>{message}</span>
      <button className="toast-close" onClick={onClose}><FiX /></button>
    </div>
  );
}

function Profile() {
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [kycDocuments, setKycDocuments] = useState([]);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    country: 'India',
    mobile: ''
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  const [kycFiles, setKycFiles] = useState({
    pan: null,
    aadhaar: null,
    selfie: null,
    address_proof: null
  });

  const [kycNumbers, setKycNumbers] = useState({
    pan: '',
    aadhaar: ''
  });

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const user = await getCurrentUser();
      if (!user) { setLoading(false); return; }

      setCurrentUser(user);
      setProfileData({
        firstName: user.profile.first_name || '',
        lastName: user.profile.last_name || '',
        email: user.profile.email || '',
        country: user.profile.country || 'India',
        mobile: user.profile.phone || ''
      });

      const docs = await getUserKYCDocuments(user.user.id);
      setKycDocuments(docs || []);
    } catch (error) {
      console.error('Error fetching user data:', error);
      showToast('Failed to load profile data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleKycFileChange = (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showToast('File size must be less than 5MB', 'error');
      e.target.value = '';
      return;
    }

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      showToast('Please upload only JPG, PNG, or PDF files', 'error');
      e.target.value = '';
      return;
    }

    setKycFiles(prev => ({ ...prev, [docType]: file }));
  };

  const handleKycNumberChange = (e) => {
    const { name, value } = e.target;
    setKycNumbers(prev => ({ ...prev, [name]: value }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) { showToast('Please login first', 'error'); return; }

    if (!profileData.firstName.trim() || !profileData.lastName.trim()) {
      showToast('First name and last name are required', 'error');
      return;
    }
    if (profileData.mobile && profileData.mobile.length < 10) {
      showToast('Please enter a valid mobile number (at least 10 digits)', 'error');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name: profileData.firstName.trim(),
          last_name: profileData.lastName.trim(),
          country: profileData.country,
          phone: profileData.mobile.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', currentUser.user.id)
        .select();

      if (error) throw error;

      showToast('Profile updated successfully!', 'success');
      setIsEditing(false);
      await fetchUserData();
    } catch (error) {
      console.error('Error updating profile:', error);
      showToast('Failed to update profile. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showToast('Passwords do not match!', 'error');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      showToast('Password must be at least 6 characters', 'error');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: passwordData.newPassword });
      if (error) throw error;

      showToast('Password updated successfully!', 'success');
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error updating password:', error);
      showToast('Failed to update password. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleKycSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) { showToast('Please login first', 'error'); return; }

    const hasFiles = kycFiles.pan || kycFiles.aadhaar || kycFiles.selfie || kycFiles.address_proof;
    if (!hasFiles) { showToast('Please select at least one document to upload', 'error'); return; }

    if (kycFiles.pan && !kycNumbers.pan.trim()) {
      showToast('Please enter PAN number', 'error'); return;
    }
    if (kycFiles.aadhaar && !kycNumbers.aadhaar.trim()) {
      showToast('Please enter Aadhaar number', 'error'); return;
    }
    if (kycNumbers.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(kycNumbers.pan)) {
      showToast('Invalid PAN format. Should be like: ABCDE1234F', 'error'); return;
    }
    if (kycNumbers.aadhaar && !/^\d{12}$/.test(kycNumbers.aadhaar)) {
      showToast('Invalid Aadhaar format. Should be 12 digits', 'error'); return;
    }

    setSaving(true);
    try {
      const uploadPromises = [];

      if (kycFiles.pan) uploadPromises.push(uploadKYCDocument({ userId: currentUser.user.id, documentType: 'pan', file: kycFiles.pan, documentNumber: kycNumbers.pan.toUpperCase() }));
      if (kycFiles.aadhaar) uploadPromises.push(uploadKYCDocument({ userId: currentUser.user.id, documentType: 'aadhaar', file: kycFiles.aadhaar, documentNumber: kycNumbers.aadhaar }));
      if (kycFiles.selfie) uploadPromises.push(uploadKYCDocument({ userId: currentUser.user.id, documentType: 'selfie', file: kycFiles.selfie, documentNumber: '' }));
      if (kycFiles.address_proof) uploadPromises.push(uploadKYCDocument({ userId: currentUser.user.id, documentType: 'proof_of_address', file: kycFiles.address_proof, documentNumber: '' }));

      const results = await Promise.all(uploadPromises);
      const failed = results.filter(r => !r.success);

      if (failed.length > 0) {
        showToast(`${failed.length} document(s) failed to upload. Please try again.`, 'error');
      } else {
        showToast('KYC documents submitted! Verification takes 24–48 hours.', 'success');
        setKycFiles({ pan: null, aadhaar: null, selfie: null, address_proof: null });
        setKycNumbers({ pan: '', aadhaar: '' });
        document.querySelectorAll('input[type="file"]').forEach(input => { input.value = ''; });
        await fetchUserData();
      }
    } catch (error) {
      console.error('Error uploading KYC:', error);
      showToast('Failed to submit KYC documents. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getKycStatus = (docType) => {
    const doc = kycDocuments.find(d => d.document_type === docType);
    return doc ? doc.status : 'not_submitted';
  };

  const getKycStatusBadge = (status) => {
    const config = {
      'approved': { text: 'Approved', cls: 'badge-approved' },
      'pending': { text: 'Pending', cls: 'badge-pending' },
      'rejected': { text: 'Rejected', cls: 'badge-rejected' },
      'not_submitted': { text: 'Not Submitted', cls: 'badge-none' }
    };
    const { text, cls } = config[status] || config['not_submitted'];
    return <span className={`status-pill ${cls}`}>{text}</span>;
  };

  const initials = `${profileData.firstName?.charAt(0) || ''}${profileData.lastName?.charAt(0) || ''}` || 'U';

  if (loading) {
    return (
      <div className="profile-page">
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading your profile…</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="profile-page">
        <div className="empty-state">
          <FiUser size={48} />
          <h2>Please log in to view your profile</h2>
          <button className="btn-primary" onClick={() => window.location.href = '/login'}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const overallKycStatus = currentUser.profile.kyc_status || 'not_submitted';

  return (
    <div className="profile-page">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="profile-header">
        <h1>Profile Settings</h1>
        <p>Manage your personal information, KYC verification, and account security.</p>
      </div>

      <div className="profile-layout">
        {/* Sidebar */}
        <aside className="profile-sidebar">
          <div className="sidebar-avatar">
            <div className="profile-avatar">{initials}</div>
            <div className="sidebar-user-info">
              <h3>{profileData.firstName} {profileData.lastName}</h3>
              <p>{profileData.email}</p>
            </div>
          </div>

          <nav className="sidebar-nav">
            <button
              className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <FiUser /> Personal Info
            </button>
            <button
              className={`nav-item ${activeTab === 'password' ? 'active' : ''}`}
              onClick={() => setActiveTab('password')}
            >
              <FiLock /> Password
            </button>
            <button
              className={`nav-item ${activeTab === 'kyc' ? 'active' : ''}`}
              onClick={() => setActiveTab('kyc')}
            >
              <FiShield /> KYC Verification
              {overallKycStatus === 'pending' && <span className="nav-badge">!</span>}
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="profile-main">

          {/* ── Profile Tab ── */}
          {activeTab === 'profile' && (
            <div className="content-card">
              <div className="card-header">
                <div>
                  <h2>Personal Information</h2>
                  <p>Update your name, country, and contact number.</p>
                </div>
                {!isEditing && (
                  <button className="btn-icon-edit" onClick={() => setIsEditing(true)}>
                    <FiEdit2 /> Edit
                  </button>
                )}
              </div>

              <form onSubmit={handleProfileSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name <span className="required">*</span></label>
                    <input
                      type="text"
                      name="firstName"
                      value={profileData.firstName}
                      onChange={handleProfileChange}
                      disabled={!isEditing}
                      required
                      placeholder="First name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name <span className="required">*</span></label>
                    <input
                      type="text"
                      name="lastName"
                      value={profileData.lastName}
                      onChange={handleProfileChange}
                      disabled={!isEditing}
                      required
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={profileData.email}
                    disabled
                  />
                  <small className="field-hint">Email address cannot be changed.</small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Country</label>
                    <select
                      name="country"
                      value={profileData.country}
                      onChange={handleProfileChange}
                      disabled={!isEditing}
                    >
                      <option value="India">India</option>
                      <option value="USA">USA</option>
                      <option value="UK">UK</option>
                      <option value="Canada">Canada</option>
                      <option value="Australia">Australia</option>
                      <option value="Singapore">Singapore</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Mobile Number <span className="required">*</span></label>
                    <input
                      type="tel"
                      name="mobile"
                      value={profileData.mobile}
                      onChange={handleProfileChange}
                      disabled={!isEditing}
                      placeholder="10-digit mobile number"
                      required
                    />
                  </div>
                </div>

                {isEditing && (
                  <div className="form-actions">
                    <button type="submit" className="btn-primary" disabled={saving}>
                      <FiSave /> {saving ? 'Saving…' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => { setIsEditing(false); fetchUserData(); }}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </form>
            </div>
          )}

          {/* ── Password Tab ── */}
          {activeTab === 'password' && (
            <div className="content-card">
              <div className="card-header">
                <div>
                  <h2>Change Password</h2>
                  <p>Use a strong password to keep your account secure.</p>
                </div>
              </div>

              <form onSubmit={handlePasswordSubmit} style={{ maxWidth: '480px' }}>
                <div className="form-group">
                  <label>New Password <span className="required">*</span></label>
                  <input
                    type="password"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="Minimum 6 characters"
                    required
                    minLength={6}
                    autoComplete="new-password"
                  />
                </div>

                <div className="form-group">
                  <label>Confirm New Password <span className="required">*</span></label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="Re-enter new password"
                    required
                    minLength={6}
                    autoComplete="new-password"
                  />
                  {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                    <small className="field-hint error-hint">Passwords do not match</small>
                  )}
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary" disabled={saving || (passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword)}>
                    <FiLock /> {saving ? 'Updating…' : 'Update Password'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ── KYC Tab ── */}
          {activeTab === 'kyc' && (
            <div className="content-card">
              <div className="card-header">
                <div>
                  <h2>KYC Verification</h2>
                  <p>Submit your documents for verification to unlock all trading features.</p>
                </div>
                <div className="kyc-overall-status">
                  <span className="overall-label">Overall Status</span>
                  {getKycStatusBadge(overallKycStatus)}
                </div>
              </div>

              <form onSubmit={handleKycSubmit}>
                <div className="kyc-grid">
                  {/* PAN */}
                  <div className="kyc-card">
                    <div className="kyc-card-header">
                      <span className="kyc-doc-label">PAN Card</span>
                      {getKycStatusBadge(getKycStatus('pan'))}
                    </div>
                    <input
                      type="text"
                      className="kyc-number-input"
                      placeholder="PAN Number (e.g. ABCDE1234F)"
                      value={kycNumbers.pan}
                      onChange={handleKycNumberChange}
                      name="pan"
                      maxLength={10}
                      style={{ textTransform: 'uppercase' }}
                    />
                    <label className="file-upload-area">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleKycFileChange(e, 'pan')}
                        hidden
                      />
                      <FiUpload className="upload-icon" />
                      <span>{kycFiles.pan ? kycFiles.pan.name : 'Click to upload'}</span>
                      <small>JPG, PNG or PDF · Max 5MB</small>
                    </label>
                  </div>

                  {/* Aadhaar */}
                  <div className="kyc-card">
                    <div className="kyc-card-header">
                      <span className="kyc-doc-label">Aadhaar Card</span>
                      {getKycStatusBadge(getKycStatus('aadhaar'))}
                    </div>
                    <input
                      type="text"
                      className="kyc-number-input"
                      placeholder="Aadhaar Number (12 digits)"
                      value={kycNumbers.aadhaar}
                      onChange={handleKycNumberChange}
                      name="aadhaar"
                      maxLength={12}
                    />
                    <label className="file-upload-area">
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleKycFileChange(e, 'aadhaar')}
                        hidden
                      />
                      <FiUpload className="upload-icon" />
                      <span>{kycFiles.aadhaar ? kycFiles.aadhaar.name : 'Click to upload'}</span>
                      <small>JPG, PNG or PDF · Max 5MB</small>
                    </label>
                  </div>

                  {/* Selfie */}
                  <div className="kyc-card">
                    <div className="kyc-card-header">
                      <span className="kyc-doc-label">Selfie / Photo</span>
                      {getKycStatusBadge(getKycStatus('selfie'))}
                    </div>
                    <label className="file-upload-area" style={{ marginTop: '44px' }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleKycFileChange(e, 'selfie')}
                        hidden
                      />
                      <FiUpload className="upload-icon" />
                      <span>{kycFiles.selfie ? kycFiles.selfie.name : 'Click to upload'}</span>
                      <small>JPG or PNG only · Max 5MB</small>
                    </label>
                  </div>

                  {/* Address Proof */}
                  <div className="kyc-card">
                    <div className="kyc-card-header">
                      <span className="kyc-doc-label">Proof of Address</span>
                      {getKycStatusBadge(getKycStatus('proof_of_address'))}
                    </div>
                    <label className="file-upload-area" style={{ marginTop: '44px' }}>
                      <input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => handleKycFileChange(e, 'address_proof')}
                        hidden
                      />
                      <FiUpload className="upload-icon" />
                      <span>{kycFiles.address_proof ? kycFiles.address_proof.name : 'Click to upload'}</span>
                      <small>Utility bill, bank statement · Max 5MB</small>
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary" disabled={saving}>
                    <FiUpload /> {saving ? 'Uploading…' : 'Submit for Verification'}
                  </button>
                </div>
              </form>

              {/* Submitted documents table */}
              {kycDocuments.length > 0 && (
                <div className="kyc-table-section">
                  <h4>Submitted Documents</h4>
                  <div className="table-wrapper">
                    <table className="kyc-table">
                      <thead>
                        <tr>
                          <th>Document</th>
                          <th>Number</th>
                          <th>Status</th>
                          <th>Submitted</th>
                          <th>Verified</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kycDocuments.map((doc) => (
                          <tr key={doc.id}>
                            <td>{doc.document_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
                            <td className="doc-number">{doc.document_number || '—'}</td>
                            <td>{getKycStatusBadge(doc.status)}</td>
                            <td>{new Date(doc.created_at).toLocaleDateString('en-IN')}</td>
                            <td>{doc.verified_at ? new Date(doc.verified_at).toLocaleDateString('en-IN') : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default Profile;